'use client';

import { c as _c } from "react/compiler-runtime";
import { animate, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@/app/components/layouts/container';
import { ALLOWED_MEDIA, AppImage } from '@/app/components/media/app-image';
import { AppVideo } from '@/app/components/media/app-video';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { Hotspots } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/engine-story/hotspots';
import type { EngineStoryProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/engine-story/types';
import { BG_CORONA_IMAGE_SRC, BG_ENGINE_STORY_IMAGE_SRC } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/fallbackConfig';
import { useSectionExit } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/useSectionExit';
import { hasPlayableData, playVideoFromGesture, primeVideo } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/autoplay';
import { applyEngineFrameOffset, ENGINE_SETTLE_DELAY_SECONDS, ENGINE_SETTLE_EASE, ENGINE_SETTLE_SECONDS, ENGINE_STORY_FRAME, getEngineFrameOffset } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/engineFrameOffset';
import { useHeroClipQueueEntry } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/clipQueue';
import { useEngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { useHeroPlayUnlock } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useHeroPlayUnlock';
import { useSectionVideoProgress } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useSectionVideoProgress';
import { useVideoBuffering } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoBuffering';
import { useVideoPlayback } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPlayback';
import { useVideoPreload } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPreload';
import { SECTION_VIDEO_BOX_CLASSNAME } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/styles';
import { SPARTAN_ENGINE_HERO_TRACKING } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { SECTION_ATMOSPHERE_FADE_OUT_SECONDS, SECTION_EXIT_SECONDS, SECTION_FADE_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
import styles from '@/app/lcv/components/sections/spartan/engine/styles.module.css';
import { SpartanDmaxTitle } from '@/app/lcv/components/sections/spartan/shared/dmax-title';
import { ENGINE_STORY_SECTION_DATA, ENGINE_STORY_VIDEO } from '@/app/lcv/contents/spartan/engine/hero.data';
import { ENGINE_IMAGE_SRC_45_ANGLE } from '@/app/lcv/contents/spartan/engine/shared/22biturbo-images.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { buildHtmlMarkup } from '@/app/utils/html/html';
import { reportRumWarning } from '@/app/utils/reportRum';
import logger from '@/lib/logger';
const STORY_VIDEO_END_PROGRESS = 0.99;
export const EngineStory = t0 => {
  const $ = _c(141);
  const {
    shouldRenderFallback: t1,
    preloadStory: t2,
    phase,
    onExitEnd,
    isActive,
    index,
    videoProgressRef,
    isProgressWatched,
    playMode,
    onBlackModeChange,
    onAnimationEnd,
    storyBreathingAnimationRef,
    onPictureReady,
    onVideoComplete,
    onHotspotPopupChange,
    onVideoBlocked,
    onBufferingChange,
    registerPlayUnlock,
    canvasBoxRef,
    engineAnchorRef,
    engineAuraVisibilityRef
  } = t0;
  const shouldRenderFallback = t1 === undefined ? false : t1;
  const preloadStory = t2 === undefined ? false : t2;
  const isReducedMotion = useReducedMotion();
  const desktop = useMediaQuery(ALLOWED_MEDIA.xlUp);
  const device = desktop ? "desktop" : "mobile";
  useHeroClipQueueEntry("storyConnection", ENGINE_STORY_SECTION_DATA.connectedSceneVideoSources.video[device]);
  useHeroClipQueueEntry("storyMain", ENGINE_STORY_SECTION_DATA.videoSources.video[device]);
  const connection = useVideoPreload(ENGINE_STORY_SECTION_DATA.connectedSceneVideoSources.video[device], preloadStory);
  const story = useVideoPreload(ENGINE_STORY_SECTION_DATA.videoSources.video[device], preloadStory);
  const connectionSrc = connection.src;
  const mainSrc = story.src;
  const videoElRef = useRef(null);
  const connectionRef = useRef(null);
  const engineSlotRef = useRef(null);
  const videoBoxRef = useRef(null);
  const engineRef = useRef(null);
  const auraWriteRef = useRef(engineAuraVisibilityRef);
  const blackModeRef = useRef(onBlackModeChange);
  const videoCompleteRef = useRef(onVideoComplete);
  const animationEndRef = useRef(onAnimationEnd);
  const [isMainReady, setIsMainReady] = useState(false);
  const [isConnectionReady, setIsConnectionReady] = useState(false);
  const [hasConnectionEnded, setHasConnectionEnded] = useState(false);
  const [sources, setSources] = useState(null);
  let t3;
  if ($[0] !== playMode) {
    t3 = () => playMode === "final";
    $[0] = playMode;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [hasStoryEnded, setHasStoryEnded] = useState(t3);
  let t4;
  if ($[2] !== playMode) {
    t4 = () => playMode === "final";
    $[2] = playMode;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [hasEngineSettled, setHasEngineSettled] = useState(t4);
  let t5;
  let t6;
  if ($[4] !== hasEngineSettled || $[5] !== isActive || $[6] !== isReducedMotion || $[7] !== storyBreathingAnimationRef) {
    t5 = () => {
      if (!storyBreathingAnimationRef || !hasEngineSettled || !isActive || isReducedMotion) {
        return;
      }
      const animation = engineRef.current?.getAnimations().find(_temp) ?? null;
      storyBreathingAnimationRef.current = animation;
      return () => {
        if (storyBreathingAnimationRef.current === animation) {
          storyBreathingAnimationRef.current = null;
        }
      };
    };
    t6 = [storyBreathingAnimationRef, hasEngineSettled, isActive, isReducedMotion];
    $[4] = hasEngineSettled;
    $[5] = isActive;
    $[6] = isReducedMotion;
    $[7] = storyBreathingAnimationRef;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  const isOnScreen = isActive || phase === "exiting";
  const isAuraLit = isOnScreen && hasStoryEnded;
  const shouldPlayClips = playMode === "full";
  const hasZoomFinished = !shouldPlayClips || hasConnectionEnded;
  const shouldPlay = isActive && shouldPlayClips && hasZoomFinished && isMainReady;
  let t7;
  if ($[10] !== onVideoBlocked || $[11] !== shouldPlay) {
    t7 = {
      videoRef: videoElRef,
      shouldPlay,
      onPlayBlocked: onVideoBlocked
    };
    $[10] = onVideoBlocked;
    $[11] = shouldPlay;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const isPlaying = useVideoPlayback(t7);
  const t8 = isActive && shouldPlayClips && isProgressWatched;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      videoRef: connectionRef
    }, {
      videoRef: videoElRef,
      endProgress: STORY_VIDEO_END_PROGRESS
    }];
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== index || $[15] !== t8 || $[16] !== videoProgressRef) {
    t10 = {
      index,
      progressRef: videoProgressRef,
      enabled: t8,
      clips: t9
    };
    $[14] = index;
    $[15] = t8;
    $[16] = videoProgressRef;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  useSectionVideoProgress(t10);
  let t11;
  if ($[18] !== onPictureReady) {
    t11 = () => onPictureReady();
    $[18] = onPictureReady;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handlePictureReady = t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = video => {
      if (!video) {
        return;
      }
      videoElRef.current = video;
      setIsMainReady(true);
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const handleMainReady = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = video_0 => {
      if (!video_0) {
        return;
      }
      connectionRef.current = video_0;
      setIsConnectionReady(true);
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleConnectionReady = t13;
  let t14;
  if ($[22] !== onPictureReady) {
    t14 = () => {
      logger.warn("[engine] story video failed, skipped");
      reportRumWarning("spartan_story_video_failed", {
        clip: "main"
      });
      setHasConnectionEnded(true);
      setHasStoryEnded(true);
      onPictureReady();
    };
    $[22] = onPictureReady;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handleMainError = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      logger.warn("[engine] connection video failed, skipped");
      reportRumWarning("spartan_story_video_failed", {
        clip: "connection"
      });
      setHasConnectionEnded(true);
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const handleConnectionError = t15;
  let t16;
  if ($[25] !== hasStoryEnded || $[26] !== isReducedMotion) {
    t16 = isReducedMotion ? undefined : cn("property-opacity-translate ease-bounce transform-gpu duration-500", {
      "translate-y-4 opacity-0": !hasStoryEnded
    });
    $[25] = hasStoryEnded;
    $[26] = isReducedMotion;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const animateInSettledUiClassName = t16;
  let t17;
  if ($[28] !== onExitEnd || $[29] !== phase) {
    t17 = {
      phase,
      onExitEnd,
      durationSeconds: SECTION_EXIT_SECONDS
    };
    $[28] = onExitEnd;
    $[29] = phase;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  useSectionExit(t17);
  let t18;
  if ($[31] !== canvasBoxRef || $[32] !== engineAnchorRef || $[33] !== isOnScreen) {
    t18 = {
      elementRef: engineSlotRef,
      canvasBoxRef,
      anchorRef: engineAnchorRef,
      enabled: isOnScreen
    };
    $[31] = canvasBoxRef;
    $[32] = engineAnchorRef;
    $[33] = isOnScreen;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  useEngineAnchor(t18);
  const t19 = isActive && shouldPlayClips && isConnectionReady && !hasConnectionEnded && !hasStoryEnded;
  let t20;
  if ($[35] !== onVideoBlocked || $[36] !== t19) {
    t20 = {
      videoRef: connectionRef,
      shouldPlay: t19,
      onPlayBlocked: onVideoBlocked
    };
    $[35] = onVideoBlocked;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  useVideoPlayback(t20);
  let t21;
  if ($[38] !== hasConnectionEnded || $[39] !== hasStoryEnded) {
    t21 = isActiveNow => {
      const isOnConnection = !hasConnectionEnded && !hasStoryEnded;
      const current = isOnConnection ? connectionRef : videoElRef;
      const next = isOnConnection ? videoElRef : connectionRef;
      if (isActiveNow) {
        playVideoFromGesture(current.current);
      } else {
        primeVideo(current.current);
      }
      primeVideo(next.current);
    };
    $[38] = hasConnectionEnded;
    $[39] = hasStoryEnded;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== connection.isResident || $[42] !== hasConnectionEnded || $[43] !== hasStoryEnded || $[44] !== story.isResident) {
    t22 = () => !hasConnectionEnded && !hasStoryEnded ? connection.isResident || hasPlayableData(connectionRef.current) : story.isResident || hasPlayableData(videoElRef.current);
    $[41] = connection.isResident;
    $[42] = hasConnectionEnded;
    $[43] = hasStoryEnded;
    $[44] = story.isResident;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t21 || $[47] !== t22) {
    t23 = {
      unlock: t21,
      isClipOnHand: t22
    };
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  useHeroPlayUnlock(registerPlayUnlock, t23);
  const t24 = isActive && shouldPlayClips && isConnectionReady && !hasConnectionEnded && !hasStoryEnded;
  let t25;
  if ($[49] !== t24) {
    t25 = {
      videoRef: connectionRef,
      enabled: t24
    };
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const isConnectionBuffering = useVideoBuffering(t25);
  const t26 = isActive && isPlaying && !hasStoryEnded;
  let t27;
  if ($[51] !== t26) {
    t27 = {
      videoRef: videoElRef,
      enabled: t26
    };
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  const isMainBuffering = useVideoBuffering(t27);
  const isBuffering = isConnectionBuffering || isMainBuffering;
  const bufferingChangeRef = useRef(onBufferingChange);
  let t28;
  if ($[53] !== onBufferingChange) {
    t28 = () => {
      bufferingChangeRef.current = onBufferingChange;
    };
    $[53] = onBufferingChange;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  useEffect(t28);
  let t29;
  let t30;
  if ($[55] !== isBuffering) {
    t29 = () => {
      if (!isBuffering) {
        return;
      }
      bufferingChangeRef.current?.(true);
      return () => bufferingChangeRef.current?.(false);
    };
    t30 = [isBuffering];
    $[55] = isBuffering;
    $[56] = t29;
    $[57] = t30;
  } else {
    t29 = $[56];
    t30 = $[57];
  }
  useEffect(t29, t30);
  let t31;
  if ($[58] !== onAnimationEnd || $[59] !== onBlackModeChange || $[60] !== onVideoComplete) {
    t31 = () => {
      blackModeRef.current = onBlackModeChange;
      videoCompleteRef.current = onVideoComplete;
      animationEndRef.current = onAnimationEnd;
    };
    $[58] = onAnimationEnd;
    $[59] = onBlackModeChange;
    $[60] = onVideoComplete;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  useEffect(t31);
  let t32;
  let t33;
  if ($[62] !== hasStoryEnded || $[63] !== isPlaying) {
    t32 = () => {
      if (!isPlaying || hasStoryEnded) {
        return;
      }
      const videoEl = videoElRef.current;
      if (!videoEl) {
        return;
      }
      let raf = 0;
      const checkProgress = () => {
        const {
          currentTime,
          duration,
          ended
        } = videoEl;
        const hasReachedEnd = ended || duration > 0 && currentTime >= duration * STORY_VIDEO_END_PROGRESS;
        if (hasReachedEnd) {
          setHasStoryEnded(true);
          return;
        }
        raf = requestAnimationFrame(checkProgress);
      };
      raf = requestAnimationFrame(checkProgress);
      return () => cancelAnimationFrame(raf);
    };
    t33 = [isPlaying, hasStoryEnded];
    $[62] = hasStoryEnded;
    $[63] = isPlaying;
    $[64] = t32;
    $[65] = t33;
  } else {
    t32 = $[64];
    t33 = $[65];
  }
  useEffect(t32, t33);
  let t34;
  let t35;
  if ($[66] !== connectionSrc || $[67] !== desktop || $[68] !== isActive || $[69] !== mainSrc || $[70] !== playMode || $[71] !== sources) {
    t34 = () => {
      if (playMode !== "full" || sources) {
        return;
      }
      if (!isActive && (!connectionSrc || !mainSrc)) {
        return;
      }
      const device_0 = desktop ? "desktop" : "mobile";
      setSources({
        connection: connectionSrc ?? ENGINE_STORY_SECTION_DATA.connectedSceneVideoSources.video[device_0],
        main: mainSrc ?? ENGINE_STORY_SECTION_DATA.videoSources.video[device_0]
      });
    };
    t35 = [isActive, playMode, sources, desktop, connectionSrc, mainSrc];
    $[66] = connectionSrc;
    $[67] = desktop;
    $[68] = isActive;
    $[69] = mainSrc;
    $[70] = playMode;
    $[71] = sources;
    $[72] = t34;
    $[73] = t35;
  } else {
    t34 = $[72];
    t35 = $[73];
  }
  useEffect(t34, t35);
  let t36;
  let t37;
  if ($[74] !== isAuraLit) {
    t36 = () => {
      const fade = animate(auraWriteRef.current, {
        value: isAuraLit ? 1 : 0
      }, {
        duration: isAuraLit ? SECTION_FADE_SECONDS : SECTION_ATMOSPHERE_FADE_OUT_SECONDS,
        ease: isAuraLit ? "easeOut" : "easeIn"
      });
      return () => fade.stop();
    };
    t37 = [isAuraLit];
    $[74] = isAuraLit;
    $[75] = t36;
    $[76] = t37;
  } else {
    t36 = $[75];
    t37 = $[76];
  }
  useEffect(t36, t37);
  let t38;
  let t39;
  if ($[77] !== hasStoryEnded || $[78] !== isReducedMotion || $[79] !== shouldPlayClips) {
    t38 = () => {
      const engine = engineRef.current;
      const slot = engineSlotRef.current;
      const videoBox = videoBoxRef.current;
      if (!engine || !slot || !videoBox) {
        return;
      }
      if (!shouldPlayClips) {
        applyEngineFrameOffset(engine, {
          x: 0,
          y: 0,
          scale: 1
        }, 1);
        setHasEngineSettled(true);
        return;
      }
      const readOffset = () => getEngineFrameOffset(videoBox.getBoundingClientRect(), slot.getBoundingClientRect(), ENGINE_STORY_FRAME);
      if (!hasStoryEnded) {
        const track = () => applyEngineFrameOffset(engine, readOffset(), 0);
        track();
        const observer = new ResizeObserver(track);
        observer.observe(videoBox);
        observer.observe(slot);
        return () => observer.disconnect();
      }
      const from = readOffset();
      if (isReducedMotion) {
        applyEngineFrameOffset(engine, from, 1);
        setHasEngineSettled(true);
        return;
      }
      const controls = animate(0, 1, {
        duration: ENGINE_SETTLE_SECONDS,
        delay: ENGINE_SETTLE_DELAY_SECONDS,
        ease: ENGINE_SETTLE_EASE,
        onUpdate: t => applyEngineFrameOffset(engine, from, t),
        onComplete: () => {
          applyEngineFrameOffset(engine, from, 1);
          setHasEngineSettled(true);
        }
      });
      return () => controls.stop();
    };
    t39 = [hasStoryEnded, shouldPlayClips, isReducedMotion];
    $[77] = hasStoryEnded;
    $[78] = isReducedMotion;
    $[79] = shouldPlayClips;
    $[80] = t38;
    $[81] = t39;
  } else {
    t38 = $[80];
    t39 = $[81];
  }
  useEffect(t38, t39);
  let t40;
  let t41;
  if ($[82] !== hasStoryEnded || $[83] !== isActive) {
    t40 = () => {
      if (!isActive || !hasStoryEnded) {
        return;
      }
      blackModeRef.current("none");
      videoCompleteRef.current();
      animationEndRef.current();
    };
    t41 = [isActive, hasStoryEnded];
    $[82] = hasStoryEnded;
    $[83] = isActive;
    $[84] = t40;
    $[85] = t41;
  } else {
    t40 = $[84];
    t41 = $[85];
  }
  useEffect(t40, t41);
  let t42;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = cn("relative h-full w-full");
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  let t43;
  if ($[87] !== shouldRenderFallback) {
    t43 = !!shouldRenderFallback && <div className={cn(SECTION_VIDEO_BOX_CLASSNAME, "after:absolute after:inset-0 after:bg-black/30")}><AppImage src={BG_ENGINE_STORY_IMAGE_SRC} alt="engine story background" fill={true} sizes="(min-width: 1280px) 640px, 50vw" /></div>;
    $[87] = shouldRenderFallback;
    $[88] = t43;
  } else {
    t43 = $[88];
  }
  let t44;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className={cn("absolute top-0 left-0 h-60 w-full bg-linear-to-b from-black to-transparent")} />;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  let t45;
  if ($[90] !== animateInSettledUiClassName) {
    t45 = cn("relative z-10 mb-10 px-4 text-center text-white lg:mb-0", animateInSettledUiClassName);
    $[90] = animateInSettledUiClassName;
    $[91] = t45;
  } else {
    t45 = $[91];
  }
  let t46;
  let t47;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.storyHeader
    });
    t47 = <SpartanDmaxTitle titleLead={ENGINE_STORY_SECTION_DATA.titleLead} title={ENGINE_STORY_SECTION_DATA.title} tone="white" className="mb-2" testId={SPARTAN_ENGINE_HERO_TRACKING.storyTitle} />;
    $[92] = t46;
    $[93] = t47;
  } else {
    t46 = $[92];
    t47 = $[93];
  }
  let t48;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="text-body-1 font-noto-bold" dangerouslySetInnerHTML={buildHtmlMarkup(ENGINE_STORY_SECTION_DATA.description)} {...createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.storyDescription
    })} />;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== t45) {
    t49 = <div className={t45} {...t46}>{t47}{t48}</div>;
    $[95] = t45;
    $[96] = t49;
  } else {
    t49 = $[96];
  }
  const t50 = !hasZoomFinished;
  let t51;
  if ($[97] !== t50) {
    t51 = cn("aspect-1-1 center-x absolute bottom-[19.5%] h-[43.75%] w-auto select-none sm:bottom-[12.5%] sm:h-[60%] lg:bottom-[8%] lg:h-[61%]", {
      "opacity-0": t50
    });
    $[97] = t50;
    $[98] = t51;
  } else {
    t51 = $[98];
  }
  let t52;
  if ($[99] !== hasEngineSettled) {
    t52 = cn("absolute inset-0 origin-top-left transform-gpu", {
      [styles.engineBreathing]: hasEngineSettled
    });
    $[99] = hasEngineSettled;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[101] !== shouldRenderFallback) {
    t53 = !!shouldRenderFallback && <div className="center-x center-y absolute size-[170%]"><AppImage src={BG_CORONA_IMAGE_SRC} alt="corona image" fill={true} sizes="(min-width: 1280px) 640px, 50vw" /></div>;
    $[101] = shouldRenderFallback;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  let t54;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <AppImage src={ENGINE_IMAGE_SRC_45_ANGLE} alt="Engine 45 Angle" width={1022} height={1022} objectFit="contain" className="aspect-1-1" sizes="60vw" testId={SPARTAN_ENGINE_HERO_TRACKING.storyEngine} />;
    $[103] = t54;
  } else {
    t54 = $[103];
  }
  let t55;
  if ($[104] !== t52 || $[105] !== t53) {
    t55 = <div ref={engineRef} className={t52}>{t53}{t54}</div>;
    $[104] = t52;
    $[105] = t53;
    $[106] = t55;
  } else {
    t55 = $[106];
  }
  const t56 = !!isActive && hasEngineSettled;
  let t57;
  if ($[107] !== onHotspotPopupChange || $[108] !== t56) {
    t57 = <Hotspots hotspots={ENGINE_STORY_SECTION_DATA.hotspotItems} isActive={t56} onPopupOpenChange={onHotspotPopupChange} />;
    $[107] = onHotspotPopupChange;
    $[108] = t56;
    $[109] = t57;
  } else {
    t57 = $[109];
  }
  let t58;
  if ($[110] !== t51 || $[111] !== t55 || $[112] !== t57) {
    t58 = <div ref={engineSlotRef} className={t51}>{t55}{t57}</div>;
    $[110] = t51;
    $[111] = t55;
    $[112] = t57;
    $[113] = t58;
  } else {
    t58 = $[113];
  }
  let t59;
  if ($[114] !== t49 || $[115] !== t58) {
    t59 = <Container className="flex grow flex-col items-center py-4 lg:py-10">{t49}{t58}</Container>;
    $[114] = t49;
    $[115] = t58;
    $[116] = t59;
  } else {
    t59 = $[116];
  }
  let t60;
  if ($[117] !== t43 || $[118] !== t59) {
    t60 = <div className="absolute inset-0 flex size-full flex-col items-center pt-14">{t43}{t44}{t59}</div>;
    $[117] = t43;
    $[118] = t59;
    $[119] = t60;
  } else {
    t60 = $[119];
  }
  let t61;
  if ($[120] !== hasStoryEnded) {
    t61 = cn(SECTION_VIDEO_BOX_CLASSNAME, "transition-opacity duration-1000 ease-out", {
      "pointer-events-none opacity-0": hasStoryEnded
    });
    $[120] = hasStoryEnded;
    $[121] = t61;
  } else {
    t61 = $[121];
  }
  let t62;
  if ($[122] !== handleMainError || $[123] !== handlePictureReady || $[124] !== sources) {
    t62 = !!sources && <AppVideo video={sources.main} poster={{
      alt: ENGINE_STORY_SECTION_DATA.videoSources.img.alt,
      mobileSrc: {
        src: ENGINE_STORY_VIDEO.cover.mobile,
        media: ALLOWED_MEDIA.xlDown
      },
      desktopSrc: {
        src: ENGINE_STORY_VIDEO.cover.desktop,
        media: ALLOWED_MEDIA.xlUp
      }
    }} paused={false} preload="auto" onElementMount={handleMainReady} onVideoPlaying={handlePictureReady} onVideoError={handleMainError} testId={SPARTAN_ENGINE_HERO_TRACKING.storyVideo} />;
    $[122] = handleMainError;
    $[123] = handlePictureReady;
    $[124] = sources;
    $[125] = t62;
  } else {
    t62 = $[125];
  }
  let t63;
  if ($[126] !== t61 || $[127] !== t62) {
    t63 = <div ref={videoBoxRef} className={t61}>{t62}</div>;
    $[126] = t61;
    $[127] = t62;
    $[128] = t63;
  } else {
    t63 = $[128];
  }
  const t64 = isPlaying || hasStoryEnded || !shouldPlayClips;
  let t65;
  if ($[129] !== t64) {
    t65 = cn(SECTION_VIDEO_BOX_CLASSNAME, "pointer-events-none", {
      "opacity-0": t64
    });
    $[129] = t64;
    $[130] = t65;
  } else {
    t65 = $[130];
  }
  let t66;
  if ($[131] !== handlePictureReady || $[132] !== sources) {
    t66 = !!sources && <AppVideo video={sources.connection} poster={{
      alt: ENGINE_STORY_SECTION_DATA.connectedSceneVideoSources.img.alt,
      mobileSrc: {
        src: ENGINE_STORY_VIDEO.introConnectedScene.cover.mobile,
        media: ALLOWED_MEDIA.xlDown
      },
      desktopSrc: {
        src: ENGINE_STORY_VIDEO.introConnectedScene.cover.desktop,
        media: ALLOWED_MEDIA.xlUp
      }
    }} paused={false} preload="auto" onElementMount={handleConnectionReady} onVideoPlaying={handlePictureReady} onVideoEnded={() => setHasConnectionEnded(true)} onVideoError={handleConnectionError} testId={SPARTAN_ENGINE_HERO_TRACKING.storyConnectionVideo} />;
    $[131] = handlePictureReady;
    $[132] = sources;
    $[133] = t66;
  } else {
    t66 = $[133];
  }
  let t67;
  if ($[134] !== t65 || $[135] !== t66) {
    t67 = <div className={t65}>{t66}</div>;
    $[134] = t65;
    $[135] = t66;
    $[136] = t67;
  } else {
    t67 = $[136];
  }
  let t68;
  if ($[137] !== t60 || $[138] !== t63 || $[139] !== t67) {
    t68 = <div className={t42}>{t60}{t63}{t67}</div>;
    $[137] = t60;
    $[138] = t63;
    $[139] = t67;
    $[140] = t68;
  } else {
    t68 = $[140];
  }
  return t68;
};
function _temp(entry) {
  return entry instanceof CSSAnimation && entry.animationName.includes("engineBreathing");
}